import { afterNextRender, computed, Directive, ElementRef, inject, input, OnDestroy, signal } from '@angular/core';

import { watchSize } from '../utils/dom.utils';

/** The edges `mibScrollFade` may fade. */
export type MibScrollFadeEdges = 'both' | 'bottom';

/**
 * Directive that fades the top and bottom edges of a vertical scroll area over 24px wherever more
 * content remains that way. Set it on an element that already scrolls; nothing fades while the
 * element does not scroll or its content fits.
 *
 * @example
 * ```html
 * <div class="overflow-y-auto" mibScrollFade>
 *   Some content taller than its box
 * </div>
 * ```
 */
@Directive({
  selector: '[mibScrollFade]',
  host: {
    '[class.mask-b-from-transparent]': 'fadeTop()',
    '[class.mask-b-to-black]': 'fadeTop()',
    '[class.mask-b-to-6]': 'fadeTop()',
    '[class.mask-t-from-transparent]': 'fadeBottom()',
    '[class.mask-t-to-black]': 'fadeBottom()',
    '[class.mask-t-to-6]': 'fadeBottom()',
  },
})
export class MibScrollFadeDirective implements OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Which edges may fade: `'both'`, or `'bottom'` to keep the top edge unfaded, e.g. under a sticky title. */
  readonly mibScrollFadeEdges = input<MibScrollFadeEdges>('both');

  /** Whether content remains above. */
  private readonly contentAbove = signal(false);
  /** Whether the top edge fades: content remains above and the edges allow it; drawn by the `mask-b` layer. */
  protected readonly fadeTop = computed(() => this.mibScrollFadeEdges() === 'both' && this.contentAbove());
  /** Whether content remains below; drawn by the `mask-t` layer, which can fade from its bottom edge. */
  protected readonly fadeBottom = signal(false);

  private stopWatching?: () => void;

  constructor() {
    afterNextRender(() => this.startWatching());
  }

  /** Stops following the scroll, the size and the content. */
  ngOnDestroy(): void {
    this.stopWatching?.();
  }

  /** Follows the scroll, the box, the content and the classes, which can switch the scroll off. */
  private startWatching(): void {
    const element = this.elementRef.nativeElement;
    const update = (): void => this.update();

    element.addEventListener('scroll', update, { passive: true });
    const stopWatchingSize = watchSize(element, update);
    const mutations = new MutationObserver(update);
    // `class` is watched so a binding that switches the scroll on or off is followed;
    // the fade's own classes cost one idle pass.
    mutations.observe(element, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    this.stopWatching = (): void => {
      element.removeEventListener('scroll', update);
      stopWatchingSize();
      mutations.disconnect();
    };
    this.update();
  }

  private update(): void {
    const element = this.elementRef.nativeElement;
    const scrolls = ['auto', 'scroll'].includes(getComputedStyle(element).overflowY);
    this.contentAbove.set(scrolls && element.scrollTop > 0);
    // The 1px allowance absorbs the sub-pixel remainder a zoomed or scaled layout leaves at the end.
    this.fadeBottom.set(scrolls && element.scrollTop + element.clientHeight < element.scrollHeight - 1);
  }
}
